import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useRef} from 'react'

import {type FileTreeCacheData, fileTreeCacheKey, loadFileTreePath} from '../queries/file-tree-query'
import {codeViewFileTreeLayoutRoute} from '../routes/code-view-file-tree-layout-route'
import {codeViewLayoutRoute} from '../routes/code-view-layout-route'

/**
 * Reads accumulated file tree data from the dedicated cache, seeded from the
 * layout on first access. Returns `loadPath` to fetch missing tree segments
 * on demand.
 */
export function useFileTree() {
  const {
    data: {fileTree: layoutFileTree, foldersToFetch: layoutFoldersToFetch, fileTreeProcessingTime},
  } = useRouteQuery(codeViewFileTreeLayoutRoute, 'mainQuery')
  const {
    data: {refInfo},
  } = useRouteQuery(codeViewLayoutRoute, 'mainQuery')
  const repo = useCurrentRepository()
  const path = usePath()
  const queryClient = useQueryClient()

  const owner = repo.ownerLogin
  const repoName = repo.name
  const branch = refInfo.name

  const cacheKey = fileTreeCacheKey(owner, repoName, branch)

  // Seed the dedicated file tree cache from layout data if not yet initialised.
  // This runs during render so that useQuery immediately has data to read.
  const initialFileTreeData: FileTreeCacheData = {
    fileTree: layoutFileTree,
    foldersToFetch: layoutFoldersToFetch,
    fileTreeProcessingTime,
  }
  if (!queryClient.getQueryData<FileTreeCacheData>(cacheKey)) {
    queryClient.setQueryData<FileTreeCacheData>(cacheKey, initialFileTreeData)
  }

  // The dedicated cache is stable (keyed by owner/repo/branch) and grows
  // monotonically via loadFileTreePath. Layout data is only used for the
  // initial seed above, not as a query dependency.
  const {data} = useQuery<FileTreeCacheData>({
    queryKey: cacheKey,
    queryFn: ({queryKey}) => queryClient.getQueryData<FileTreeCacheData>(queryKey)!,
    staleTime: Infinity,
    initialData: initialFileTreeData,
  })

  const loadPath = useCallback(
    async (targetPath: string) => {
      await loadFileTreePath({owner, repo: repoName, branch, path: targetPath})
    },
    [owner, repoName, branch],
  )

  // Fetch missing segments for the current path after render
  const previousPathRef = useRef(path)
  useEffect(() => {
    // Only load when the path changes (or on initial mount)
    if (previousPathRef.current !== path || !data.fileTree[path]) {
      previousPathRef.current = path
      loadPath(path)
    }
  }, [path, loadPath, data.fileTree])

  return {
    fileTree: data.fileTree,
    foldersToFetch: data.foldersToFetch,
    fileTreeProcessingTime: data.fileTreeProcessingTime,
    loadPath,
  }
}
